import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { BackButton } from "../../components/common/BackButton";
import { getStripePromise } from "../../shared/stripe";
import {
  getShopCartTotalCents,
  useShopCartStore,
  type ShopCartItem,
} from "../../stores/shopCart.store";
import * as SentryUtils from "../../utils/sentry";
import { formatShopPrice } from "./catalogue";
import { startShopCheckout } from "./shop.service";
import { ActionButton, Container, StateMessage } from "./shop.styles";
import { useShopAccess } from "./useShopAccess";

// The cart uses the same centred column as the product detail page.
const CartContainer = styled(Container)`
  max-width: 1188px;
  padding-top: 20px;

  @media (min-width: 768px) {
    padding-top: 28px;
  }
`;

const Title = styled.h1`
  color: #000;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  margin: 0 0 20px;
  text-transform: uppercase;
`;

const Intro = styled.p`
  color: #000;
  font-size: 14px;
  line-height: 1.35;
  margin: 0 0 28px;
  max-width: 744px;

  @media (min-width: 900px) {
    margin-bottom: 48px;
  }
`;

const Layout = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (min-width: 900px) {
    flex-direction: row;
    gap: 64px;
  }
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;

  @media (min-width: 900px) {
    flex: 1 1 auto;
    max-width: 744px;
  }
`;

const LineDivider = styled.div<{ $isEdge: boolean }>`
  background: #000;
  height: ${(props) => (props.$isEdge ? "1px" : "0.5px")};
`;

// Mobile keeps the stepper and the line total on their own row under the
// details; from 640px the same cells spread into the desktop row.
const Line = styled.div`
  align-items: start;
  display: grid;
  gap: 12px 14px;
  grid-template-areas:
    "image details"
    "image controls";
  grid-template-columns: 72px minmax(0, 1fr);
  padding: 16px 0;

  @media (min-width: 640px) {
    align-items: center;
    gap: 24px;
    grid-template-areas: "image details stepper total";
    grid-template-columns: 120px minmax(0, 1fr) auto auto;
    padding: 20px 0;
  }
`;

const LineImage = styled.img`
  border: 0.5px solid #000;
  grid-area: image;
  height: 72px;
  object-fit: cover;
  width: 72px;

  @media (min-width: 640px) {
    height: 120px;
    width: 120px;
  }
`;

const LineDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-area: details;
  min-width: 0;

  @media (min-width: 640px) {
    gap: 10px;
  }
`;

const LineName = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
`;

const LineVariant = styled.span`
  color: #000;
  font-size: 14px;
  line-height: 1.2;
  text-transform: uppercase;
`;

const LineUnitPrice = styled.span`
  color: #000;
  font-size: 12px;
  font-weight: 300;
  line-height: 1.2;
  text-transform: uppercase;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #000;
  cursor: pointer;
  font-size: 12px;
  font-weight: 300;
  line-height: 1;
  padding: 0;
  text-align: left;
  text-decoration: underline;
  text-transform: uppercase;
  width: fit-content;
`;

// On mobile the stepper and the line total share a row under the details, so
// quantity stays editable on every screen size. From 640px the wrapper steps
// out of the way and its children take their own cells in the row.
const LineControls = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  grid-area: controls;
  justify-content: space-between;

  @media (min-width: 640px) {
    display: contents;
  }
`;

const QuantityStepper = styled.div`
  display: flex;
  flex-shrink: 0;
  grid-area: stepper;
`;

const StepperButton = styled.button`
  align-items: center;
  background: none;
  border: 1px solid #000;
  color: #000;
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  height: 36px;
  justify-content: center;
  line-height: 1;
  width: 36px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.38;
  }

  @media (min-width: 640px) {
    height: 43px;
    width: 43px;
  }
`;

const StepperValue = styled.span`
  align-items: center;
  border-bottom: 1px solid #000;
  border-top: 1px solid #000;
  color: #000;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  height: 36px;
  justify-content: center;
  line-height: 1;
  width: 48px;

  @media (min-width: 640px) {
    height: 43px;
    width: 60px;
  }
`;

const LineTotal = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 600;
  grid-area: total;
  line-height: 1;

  @media (min-width: 640px) {
    text-align: right;
    width: 100px;
  }
`;

const NoteField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 26px;

  @media (min-width: 900px) {
    margin-top: 32px;
    max-width: 504px;
  }
`;

const NoteLabel = styled.label`
  color: #000;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
`;

const NoteInput = styled.textarea`
  border: 1px solid #000;
  color: #000;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.35;
  padding: 10px 12px;
  resize: vertical;
  width: 100%;
`;

const NoteHint = styled.span`
  color: #000;
  font-size: 12px;
  font-weight: 300;
  line-height: 1.3;
`;

const Summary = styled.div`
  border: 1px solid #000;
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: 900px) {
    flex: 0 0 348px;
    width: 348px;
  }
`;

const SummaryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

const SummaryTitle = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
`;

const SummaryRow = styled.div`
  color: #000;
  display: flex;
  font-size: 14px;
  gap: 12px;
  justify-content: space-between;
  line-height: 1.2;
  text-transform: uppercase;
`;

const SummaryDivider = styled.div`
  background: #000;
  height: 0.5px;
`;

const SummaryTotalRow = styled(SummaryRow)`
  font-size: 20px;
  font-weight: 700;
`;

const PickupNotice = styled.p`
  color: #000;
  font-size: 14px;
  line-height: 1.35;
  margin: 0;
  text-transform: uppercase;
`;

const PayButton = styled(ActionButton)`
  border-radius: 0;
  min-height: 43px;
  width: 100%;
`;

const PaymentHint = styled.span`
  color: #000;
  display: block;
  font-size: 12px;
  font-weight: 300;
  line-height: 1.3;
  padding: 14px 24px 20px;
  text-align: center;
  text-transform: uppercase;
`;

const CheckoutFeedback = styled.p`
  color: #000;
  font-size: 13px;
  line-height: 1.35;
  margin: 0;
  padding: 0 24px 20px;
  text-align: center;
`;

const EmptyActions = styled.div`
  margin-top: 8px;
`;

const MAX_NOTE_LENGTH = 500;

export const Cart = () => {
  const navigate = useNavigate();
  const access = useShopAccess();
  const items = useShopCartStore((state) => state.items);
  const setCartItem = useShopCartStore((state) => state.setCartItem);
  const clearCart = useShopCartStore((state) => state.clear);
  const isRestoring = useShopCartStore((state) => state.isRestoring);
  const [checkoutFeedback, setCheckoutFeedback] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [note, setNote] = useState("");

  const totalCents = getShopCartTotalCents(items);

  // Once Stripe is on screen the cart is already emptied, so the embedded
  // checkout has to be shown before any of the empty cart branches below.
  if (clientSecret) {
    return (
      <CartContainer>
        <EmbeddedCheckoutProvider
          stripe={getStripePromise()}
          options={{ clientSecret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </CartContainer>
    );
  }

  // The profile of a signed in account is still loading: nothing is known yet
  // about its membership, so the page waits instead of guessing.
  if (access === "unknown") {
    return (
      <CartContainer>
        <StateMessage>Caricamento...</StateMessage>
      </CartContainer>
    );
  }

  // Only a tesserato account has a cart: everyone else gets the page that
  // tells them what is missing instead.
  if (access !== "member") {
    return (
      <CartContainer>
        <BackButton onClick={() => navigate("/shop")} />
        <Title>Carrello</Title>
        <Intro>
          {access === "visitor"
            ? "Per usare il carrello devi accedere con il tuo account. Accedi e aggiungi di nuovo i prodotti che ti interessano."
            : "Il carrello è riservato ai tesserati: almeno un membro della famiglia deve avere la tessera valida per la stagione in corso."}
        </Intro>
        <EmptyActions>
          <ActionButton
            onClick={() =>
              navigate(access === "visitor" ? "/login" : "/account/membership")
            }
          >
            {access === "visitor" ? "Accedi o registrati" : "Vai al tesseramento"}
          </ActionButton>
        </EmptyActions>
      </CartContainer>
    );
  }

  if (isRestoring) {
    return (
      <CartContainer>
        <StateMessage>Caricamento...</StateMessage>
      </CartContainer>
    );
  }

  if (items.length === 0) {
    return (
      <CartContainer>
        <BackButton onClick={() => navigate("/shop")} />
        <Title>Carrello</Title>
        <Intro>
          Il carrello è vuoto. Aggiungi i prodotti dalle pagine di dettaglio per
          completare un ordine.
        </Intro>
        <EmptyActions>
          <ActionButton onClick={() => navigate("/shop")}>
            Torna allo shop
          </ActionButton>
        </EmptyActions>
      </CartContainer>
    );
  }

  const changeQuantity = (item: ShopCartItem, quantity: number) => {
    setCheckoutFeedback(null);
    void setCartItem(
      item.productId,
      item.variantId,
      Math.min(Math.max(quantity, 0), item.maxQuantity),
    );
  };

  const handleCheckout = async () => {
    setCheckoutFeedback(null);
    setIsStartingCheckout(true);

    try {
      await getStripePromise();

      const secret = await startShopCheckout(
        items.map((item) => {
          return {
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity,
          };
        }),
        note,
      );

      // The cart has done its job: it prepared this checkout. Emptying it here
      // covers both outcomes at once, so a buyer who abandons Stripe comes back
      // to an empty cart and a buyer who pays never sees the old one again.
      clearCart();
      setClientSecret(secret);
    } catch (error) {
      SentryUtils.captureException(error, {
        tags: { component: "ShopCart", operation: "checkout_session_creation" },
        extra: { itemCount: items.length, totalCents },
      });
      setCheckoutFeedback(
        error instanceof Error
          ? error.message
          : "Non è stato possibile avviare il pagamento",
      );
    } finally {
      setIsStartingCheckout(false);
    }
  };

  return (
    <CartContainer>
      <BackButton onClick={() => navigate("/shop")} />
      <Title>Carrello</Title>
      <Layout>
        <Lines>
          <LineDivider $isEdge={true} />
          {items.map((line, index) => (
            <div key={`${line.productId}-${line.variantId}`}>
              <Line>
                <LineImage alt={line.productName} src={line.imageUrl} />
                <LineDetails>
                  <LineName>{line.productName}</LineName>
                  <LineVariant>Taglia {line.variantLabel}</LineVariant>
                  <LineUnitPrice>
                    {formatShopPrice(line.unitPriceCents)} cad.
                  </LineUnitPrice>
                  <RemoveButton
                    onClick={() => {
                      setCheckoutFeedback(null);
                      void setCartItem(line.productId, line.variantId, 0);
                    }}
                    type="button"
                  >
                    Rimuovi
                  </RemoveButton>
                </LineDetails>
                <LineControls>
                    <QuantityStepper>
                      <StepperButton
                        aria-label={`Diminuisci quantità di ${line.productName} ${line.variantLabel}`}
                        disabled={line.quantity <= 1}
                        onClick={() => changeQuantity(line, line.quantity - 1)}
                        type="button"
                      >
                        -
                      </StepperButton>
                      <StepperValue
                        aria-label={`Quantità di ${line.productName} ${line.variantLabel}`}
                      >
                        {line.quantity}
                      </StepperValue>
                      <StepperButton
                        aria-label={`Aumenta quantità di ${line.productName} ${line.variantLabel}`}
                        disabled={line.quantity >= line.maxQuantity}
                        onClick={() => changeQuantity(line, line.quantity + 1)}
                        type="button"
                      >
                        +
                      </StepperButton>
                    </QuantityStepper>
                    <LineTotal>
                      {formatShopPrice(line.unitPriceCents * line.quantity)}
                    </LineTotal>
                </LineControls>
              </Line>
              <LineDivider $isEdge={index === items.length - 1} />
            </div>
          ))}
          <NoteField>
            <NoteLabel htmlFor="shop-cart-note">
              Note per il ritiro (facoltative)
            </NoteLabel>
            <NoteInput
              id="shop-cart-note"
              maxLength={MAX_NOTE_LENGTH}
              onChange={(event) =>
                setNote(event.target.value.slice(0, MAX_NOTE_LENGTH))
              }
              placeholder="Es. ritiro il sabato in sede"
              rows={3}
              value={note}
            />
            <NoteHint>
              {note.length}/{MAX_NOTE_LENGTH} caratteri
            </NoteHint>
          </NoteField>
        </Lines>
        <Summary>
          <SummaryBody>
            <SummaryTitle>Riepilogo</SummaryTitle>
            {items.map((line) => (
              <SummaryRow key={`summary-${line.productId}-${line.variantId}`}>
                <span>
                  {line.productName} · {line.variantLabel}
                </span>
                <span>
                  {line.quantity} × {formatShopPrice(line.unitPriceCents)}
                </span>
              </SummaryRow>
            ))}
            <SummaryDivider />
            <SummaryTotalRow>
              <span>Totale</span>
              <span>{formatShopPrice(totalCents)}</span>
            </SummaryTotalRow>
            <PickupNotice>
              Non è prevista spedizione. Per il ritiro contattaci
              telefonicamente oppure presso il negozio DaiFradei di William e
              Fabio a Gavardo.
            </PickupNotice>
          </SummaryBody>
          <PayButton
            fullWidth
            loading={isStartingCheckout}
            loadingText="Apertura pagamento..."
            onClick={() => void handleCheckout()}
            variant="danger"
          >
            Paga {formatShopPrice(totalCents)}
          </PayButton>
          {checkoutFeedback ? (
            <CheckoutFeedback aria-live="polite">
              {checkoutFeedback}
            </CheckoutFeedback>
          ) : (
            <PaymentHint>Pagamento gestito da Stripe</PaymentHint>
          )}
        </Summary>
      </Layout>
    </CartContainer>
  );
};
